// One recipe card for a whole screen. A page wraps itself in
// <RecipeCardHost>; any line below it renders <DishRecipeButton> or
// <PrepRecipeButton> and the host opens the card.
//
// The buttons render NOTHING unless recipes are on for this tenant (the
// deploy's flag and the admin's switch) and the item actually has a recipe —
// and nothing at all outside a host — so a screen that adds one is unchanged
// for every tenant that has not asked for recipe cards.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useRecipeCardLookup, type RecipeCardLookup } from '@/hooks/useRecipeCards'
import type { RecipeCardOption, RecipeCardTarget } from '@/lib/recipeCard/recipeCardTypes'
import { RecipeCardButton } from './RecipeCardButton'
import { RecipeCardSheet } from './RecipeCardSheet'

interface HostValue extends RecipeCardLookup {
  open: (target: RecipeCardTarget) => void
}

const HostContext = createContext<HostValue | null>(null)

export function RecipeCardHost({ children }: { children: ReactNode }) {
  const lookup = useRecipeCardLookup()
  const [target, setTarget] = useState<RecipeCardTarget | null>(null)
  const open = useCallback((t: RecipeCardTarget) => setTarget(t), [])
  const value = useMemo(() => ({ ...lookup, open }), [lookup, open])
  return (
    <HostContext.Provider value={value}>
      {children}
      {lookup.enabled && <RecipeCardSheet target={target} onClose={() => setTarget(null)} />}
    </HostContext.Provider>
  )
}

/** A dish on a production plan line: opens scaled to the planned portions. */
export function DishRecipeButton({ productId, name, qty, orderType, options, className }: {
  productId: string
  name: string
  qty?: number
  orderType?: string
  options?: RecipeCardOption[]
  className?: string
}) {
  const host = useContext(HostContext)
  if (!host || !host.hasDish(productId)) return null
  return (
    <RecipeCardButton
      name={name}
      className={className}
      onOpen={() => host.open({ kind: 'product', productId, name, qty, orderType, options })}
    />
  )
}

/** A prep item: opens scaled to `qty` of it, in its own unit. */
export function PrepRecipeButton({ prepItemId, name, qty, className }: {
  prepItemId: string
  name: string
  qty?: number
  className?: string
}) {
  const host = useContext(HostContext)
  if (!host || !host.hasPrep(prepItemId)) return null
  return (
    <RecipeCardButton
      name={name}
      className={className}
      onOpen={() => host.open({ kind: 'prep', prepItemId, name, qty })}
    />
  )
}
